// Per-field auto-save for the profile: one save state per field, the PUT that
// carries only the keys it is given, and the three save shapes (text on
// blur/Enter, toggle-with-revert, the Enter → blur helper). Split out of
// pages/ProfilePage.tsx; every sheet saves through this.
import React, { useState } from "react";
import { usersApi } from "../services/users";
import type { User } from "../types/User";
import type { SaveState } from "../components/FieldStatus";
import { t } from "../i18n";

type Args = {
  user: User | null;
  setProfile: React.Dispatch<React.SetStateAction<User | null>>;
};

export function useProfileAutosave({ user, setProfile }: Args) {
  const [fieldStatus, setFieldStatus] = useState<Record<string, SaveState>>({});

  // ===== Per-field auto-save =====
  // Every editable field saves itself, independently. One field failing never
  // blocks another — this replaces the old single "Update profile" button that
  // chained profile → price → currency and dropped later steps on any earlier
  // failure (the silent currency-revert bug).

  // A good save says nothing (founder, 1.5.2: the field is simply what was
  // typed). It clears the field's in-flight state or an earlier error.
  const markSaved = (key: string) =>
    setFieldStatus((s) => {
      if (!(key in s)) return s;
      const next = { ...s };
      delete next[key];
      return next;
    });

  const setSaving = (key: string) =>
    setFieldStatus((s) => ({ ...s, [key]: { state: "saving" } }));

  const setSaveError = (key: string, msg: string) =>
    setFieldStatus((s) => ({ ...s, [key]: { state: "error", msg } }));

  const NETWORK_ERR = t("useProfileAutosave.couldnTSaveCheck");

  // Save one or more plain profile fields via PUT /users/:slug. The server
  // only updates the keys it receives, so this is safe to call per-field.
  const putProfileField = async (
    key: string,
    fields: Record<string, string>
  ): Promise<boolean> => {
    if (!user) return false;
    setSaving(key);
    try {
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      await usersApi.update(user.slug, fd);
      markSaved(key);
      // Broadcast to tabs mounted UNDER the profile drawer: they keep
      // their own profile copy and otherwise stay stale until a full app
      // restart (card size used to need a relaunch).
      window.dispatchEvent(new CustomEvent("profile:patched", { detail: fields }));
      return true;
    } catch (e: any) {
      console.error(`Profile save failed (${key}):`, e);
      // A typed Instagram the server won't keep (both Instagram switches came
      // on meanwhile, build 54): its own line, never "check your connection".
      const refused = e?.response?.data?.code === "instagram_verify_required" && e.response.data.msg;
      setSaveError(key, refused || NETWORK_ERR);
      return false;
    }
  };

  // Text fields (name, bio, venue, instagram): save on blur/Enter, only when
  // the value actually changed.
  const saveTextField = async (
    key: string,
    field: string,
    value: string,
    lastSaved: string
  ) => {
    if (value === lastSaved) return;
    const ok = await putProfileField(key, { [field]: value });
    if (ok) setProfile((p) => (p ? ({ ...p, [field]: value } as User) : p));
  };

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") e.currentTarget.blur();
  };

  // Toggles: flip optimistically, save, and flip back on failure.
  const toggleField = async (
    key: string,
    field: string,
    current: boolean,
    setter: (v: boolean) => void
  ) => {
    const next = !current;
    setter(next);
    const ok = await putProfileField(key, { [field]: String(next) });
    if (!ok) setter(current);
  };

  return { fieldStatus, markSaved, setSaving, setSaveError, NETWORK_ERR, putProfileField, saveTextField, blurOnEnter, toggleField };
}

export type ProfileAutosave = ReturnType<typeof useProfileAutosave>;
